import type { ConnectionProfile } from '@k-ucap/shared';
import type { ConnectionProfileRepository } from './RepositoryInterfaces.js';

export class InMemoryConnectionProfileRepository implements ConnectionProfileRepository {
  private profiles: Map<string, ConnectionProfile> = new Map();

  async findAll(): Promise<ConnectionProfile[]> {
    return Array.from(this.profiles.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  async findById(id: string): Promise<ConnectionProfile | undefined> {
    return this.profiles.get(id);
  }

  async findByProjectId(projectId: string): Promise<ConnectionProfile[]> {
    return Array.from(this.profiles.values())
      .filter((p) => p.projectId === projectId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async save(profile: ConnectionProfile): Promise<ConnectionProfile> {
    const existing = this.profiles.get(profile.id);
    const saved: ConnectionProfile = {
      ...profile,
      createdAt: existing?.createdAt ?? profile.createdAt,
      updatedAt: new Date().toISOString(),
    };
    this.profiles.set(profile.id, saved);
    return saved;
  }

  async delete(id: string): Promise<void> {
    this.profiles.delete(id);
  }

  async duplicate(id: string, newName?: string): Promise<ConnectionProfile | undefined> {
    const original = this.profiles.get(id);
    if (!original) return undefined;
    const duplicated: ConnectionProfile = {
      ...original,
      id: `${original.id}-copy-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.profiles.set(duplicated.id, duplicated);
    return duplicated;
  }
}

export const createInMemoryConnectionProfileRepository = (): InMemoryConnectionProfileRepository =>
  new InMemoryConnectionProfileRepository();
